import React, { useEffect, useRef, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Plus, X, AlertTriangle, RotateCcw } from "lucide-react";

const fmtEUR = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const EVENT_LABEL = { departure: "Départ", return: "Retour" };
const key = (e) => `${e.surcharge_id}|${e.event}`;

/**
 * Sélecteur de surcoûts tarifaires pour le formulaire réservation/devis.
 * Détecte automatiquement les surcoûts applicables au départ et au retour (règle
 * Junago : évaluation aux seuls événements), tout en respectant les décisions
 * manuelles du loueur (retrait, ajout). Ne réajoute jamais silencieusement un
 * surcoût volontairement retiré.
 */
export default function SurchargesPicker({ context, entries, onChange }) {
  const { pickup_location_id, return_location_id, start_date, start_time, end_date, end_time } = context;
  const [evalResult, setEvalResult] = useState(null);
  const [addOpen, setAddOpen] = useState(null); // 'departure' | 'return' | null
  const lastKey = useRef("");

  // Évaluation serveur des surcoûts applicables (débattue sur le contexte)
  useEffect(() => {
    if (!pickup_location_id || !start_date || !end_date) { setEvalResult(null); return; }
    const ctxKey = JSON.stringify({ pickup_location_id, return_location_id, start_date, start_time, end_date, end_time });
    const t = setTimeout(() => {
      api.post("/vendor/surcharges/evaluate", {
        pickup_location_id, return_location_id: return_location_id || pickup_location_id,
        start_date, start_time: start_time || "", end_date, end_time: end_time || "",
      }).then(({ data }) => { lastKey.current = ctxKey; setEvalResult(data); }).catch(() => setEvalResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [pickup_location_id, return_location_id, start_date, start_time, end_date, end_time]);

  // Fusion : auto-détection + respect des décisions manuelles (retrait/ajout).
  useEffect(() => {
    if (!evalResult) return;
    const existingKeys = new Set(entries.map(key));
    let next = [...entries];
    for (const event of ["departure", "return"]) {
      const ev = evalResult[event];
      if (!ev || !ev.agency_id) continue;
      const applicable = ev.applicable || [];
      const appIds = new Set(applicable.map((a) => a.surcharge_id));
      const availIds = new Set((ev.available || []).map((a) => a.surcharge_id));
      // Ajout des surcoûts auto nouvellement applicables (jamais ceux déjà retirés)
      for (const a of applicable) {
        const k = `${a.surcharge_id}|${event}`;
        if (!existingKeys.has(k)) {
          next.push({ surcharge_id: a.surcharge_id, event, name: a.name, amount: a.amount, agency_id: ev.agency_id, origin: "auto", state: "applied" });
          existingKeys.add(k);
        }
      }
      next = next.map((e) => {
        if (e.event !== event) return e;
        // Auto appliqué devenu non applicable → retiré de la liste (plus bas via filtre)
        if (e.origin === "manual" && !availIds.has(e.surcharge_id)) {
          return { ...e, state: "incoherent", agency_id: ev.agency_id };
        }
        if (e.origin === "manual" && e.state === "incoherent" && availIds.has(e.surcharge_id)) {
          return { ...e, state: "applied" };
        }
        return e;
      });
      next = next.filter((e) => !(e.event === event && e.origin === "auto" && e.state === "applied" && !appIds.has(e.surcharge_id)));
    }
    if (JSON.stringify(next) !== JSON.stringify(entries)) onChange(next);
  }, [evalResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const setState = (k, state) => onChange(entries.map((e) => (key(e) === k ? { ...e, state } : e)));
  const remove = (k) => onChange(entries.filter((e) => key(e) !== k));
  const addManual = (event, s, agencyId) => {
    const k = `${s.surcharge_id}|${event}`;
    if (entries.some((e) => key(e) === k)) { onChange(entries.map((e) => (key(e) === k ? { ...e, state: "applied", origin: "manual" } : e))); }
    else onChange([...entries, { surcharge_id: s.surcharge_id, event, name: s.name, amount: s.amount, agency_id: agencyId, origin: "manual", state: "applied" }]);
    setAddOpen(null);
  };

  const total = useMemo(() => entries.filter((e) => e.state === "applied").reduce((s, e) => s + Number(e.amount || 0), 0), [entries]);

  if (!pickup_location_id || !start_date || !end_date) return null;

  const availableFor = (event) => {
    const ev = evalResult?.[event];
    if (!ev) return [];
    const present = new Set(entries.filter((e) => e.event === event && e.state !== "removed").map((e) => e.surcharge_id));
    return (ev.available || []).filter((a) => !present.has(a.surcharge_id));
  };

  const EVENT_ORDER = { departure: 0, return: 1 };
  const visible = entries
    .filter((e) => e.state !== "removed" || e.origin === "auto")
    .slice()
    .sort((a, b) => (EVENT_ORDER[a.event] - EVENT_ORDER[b.event]) || (a.origin === b.origin ? 0 : a.origin === "auto" ? -1 : 1));
  const hasAnyAvailable = (availableFor("departure").length + availableFor("return").length) > 0;

  if (visible.length === 0 && !hasAnyAvailable) return null;

  const renderAdd = (event) => {
    const opts = availableFor(event);
    const agencyId = evalResult?.[event]?.agency_id;
    if (opts.length === 0 || !agencyId) return null;
    return (
      <div className="relative inline-block">
        <button type="button" onClick={() => setAddOpen(addOpen === event ? null : event)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:text-violet-700" data-testid={`surcharge-add-${event}`}>
          <Plus className="h-3 w-3" /> Ajouter ({EVENT_LABEL[event].toLowerCase()})
        </button>
        {addOpen === event && (
          <div className="absolute z-20 mt-1 right-0 w-56 rounded-xl border border-slate-200 bg-white shadow-lg p-1" data-testid={`surcharge-add-menu-${event}`}>
            {opts.map((o) => (
              <button key={o.surcharge_id} type="button" onClick={() => addManual(event, o, agencyId)} className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-violet-50 text-xs flex items-center justify-between" data-testid={`surcharge-add-opt-${event}-${o.surcharge_id}`}>
                <span className="truncate text-slate-700">{o.name}</span>
                <span className="font-semibold text-slate-500 shrink-0 ml-2">{fmtEUR(o.amount)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pt-2 mt-1 border-t border-slate-100" data-testid="surcharges-picker">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Surcoûts tarifaires</span>
        <div className="flex items-center gap-3">{renderAdd("departure")}{renderAdd("return")}</div>
      </div>
      {visible.length === 0 ? (
        <div className="text-[11px] text-slate-400 pb-1" data-testid="surcharges-none">Aucun surcoût applicable au départ ni au retour.</div>
      ) : (
        <div className="space-y-1">
          {visible.map((e) => {
            const k = key(e);
            const applied = e.state === "applied";
            const incoherent = e.state === "incoherent";
            return (
              <div key={k} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`surcharge-row-${e.event}-${e.surcharge_id}`}>
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${e.event === "departure" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{EVENT_LABEL[e.event]}</span>
                  <span className={`truncate ${applied ? "text-slate-600" : "text-slate-400 line-through"}`}>{e.name}</span>
                  {e.origin === "manual" && !incoherent && <span className="shrink-0 text-[9px] text-violet-500 font-semibold">manuel</span>}
                  {incoherent && <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] text-red-500 font-semibold" title="Ce surcoût n'est pas disponible pour l'agence de cet événement"><AlertTriangle className="h-2.5 w-2.5" /> non disponible</span>}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`font-semibold tabular-nums ${applied ? "text-slate-700" : "text-slate-300"}`}>+ {fmtEUR(e.amount)}</span>
                  {incoherent ? (
                    <button type="button" onClick={() => remove(k)} className="text-slate-400 hover:text-red-600" data-testid={`surcharge-remove-${e.event}-${e.surcharge_id}`} title="Retirer"><X className="h-3.5 w-3.5" /></button>
                  ) : applied ? (
                    <button type="button" onClick={() => setState(k, "removed")} className="text-slate-400 hover:text-red-600" data-testid={`surcharge-toggle-${e.event}-${e.surcharge_id}`} title="Retirer ce surcoût"><X className="h-3.5 w-3.5" /></button>
                  ) : (
                    <button type="button" onClick={() => setState(k, "applied")} className="text-slate-400 hover:text-emerald-600" data-testid={`surcharge-toggle-${e.event}-${e.surcharge_id}`} title="Rétablir ce surcoût"><RotateCcw className="h-3.5 w-3.5" /></button>
                  )}
                </span>
              </div>
            );
          })}
          {total > 0 && (
            <div className="flex items-center justify-between text-[12px] pt-0.5" data-testid="surcharges-total-row">
              <span className="text-slate-500 font-medium">Total des surcoûts</span>
              <span className="font-semibold text-slate-700 tabular-nums">+ {fmtEUR(total)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
